import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Plus, Trash2, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { toast } from "sonner";

export default function BannerManager({ banners = [], onUpdate }) {
  const [newBanner, setNewBanner] = useState({
    name: '',
    image_url: '',
    link: '',
    active: true,
    position: 'home_top'
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setNewBanner({ ...newBanner, image_url: file_url });
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!newBanner.name || !newBanner.image_url) {
      toast.error('Completa nombre e imagen');
      return;
    }

    try {
      await base44.entities.Banner.create(newBanner);
      setNewBanner({ name: '', image_url: '', link: '', active: true, position: 'home_top' });
      onUpdate();
      toast.success('Banner creado');
    } catch (error) {
      toast.error('Error al crear banner');
    }
  };

  const handleDelete = async (id) => {
    try {
      await base44.entities.Banner.delete(id);
      onUpdate();
      toast.success('Banner eliminado');
    } catch (error) {
      toast.error('Error al eliminar banner');
    }
  };

  const toggleActive = async (banner) => {
    try {
      await base44.entities.Banner.update(banner.id, { active: !banner.active });
      onUpdate();
      toast.success(banner.active ? 'Banner desactivado' : 'Banner activado');
    } catch (error) {
      toast.error('Error al actualizar banner');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Crear Nuevo Banner</h3>
        <div className="space-y-3">
          <Input
            placeholder="Nombre del banner"
            value={newBanner.name}
            onChange={(e) => setNewBanner({ ...newBanner, name: e.target.value })}
          />
          
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Imagen del banner
            </label>
            <div className="flex gap-3 items-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="banner-upload"
              />
              <label htmlFor="banner-upload">
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('banner-upload').click();
                  }}
                  className="cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Subiendo...' : 'Subir Imagen'}
                </Button>
              </label>
              {newBanner.image_url && (
                <img src={newBanner.image_url} alt="Preview" className="h-12 rounded" />
              )}
            </div>
          </div>

          <Input
            placeholder="URL de destino (https://...)"
            value={newBanner.link}
            onChange={(e) => setNewBanner({ ...newBanner, link: e.target.value })}
          />

          <Button onClick={handleCreate} className="w-full bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Crear Banner
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Banners Activos</h3>
        <div className="space-y-3">
          {banners.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No hay banners creados</p>
          ) : (
            banners.map((banner) => (
              <div key={banner.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img src={banner.image_url} alt={banner.name} className="w-20 h-12 rounded object-cover" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{banner.name}</p>
                  {banner.link && (
                    <a href={banner.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1 hover:underline">
                      <ExternalLink className="w-3 h-3" />
                      {banner.link}
                    </a>
                  )}
                  <span className={`text-xs ${banner.active ? 'text-green-600' : 'text-gray-400'}`}>
                    {banner.active ? '● Activo' : '○ Inactivo'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleActive(banner)}
                  >
                    {banner.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(banner.id)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}